import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Stars } from "./Stars";
import { toast } from "sonner";
import { Upload, MessageSquare, X } from "lucide-react";

interface Review {
  id: string;
  product_id: string;
  user_id: string | null;
  customer_name: string;
  rating: number;
  comment: string | null;
  image_url: string | null;
  status: string;
  admin_reply: string | null;
  created_at: string;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export const ProductReviews = ({ productId }: { productId: string }) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("product_reviews")
      .select("*")
      .eq("product_id", productId)
      .eq("status", "approved")
      .order("created_at", { ascending: false });
    setReviews((data ?? []) as Review[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [productId]);

  useEffect(() => {
    if (!user) return;
    const meta = (user.user_metadata as any) ?? {};
    setName(meta.full_name || user.email?.split("@")[0] || "Customer");
  }, [user]);

  const handleImage = async (file: File) => {
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `reviews/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("product-images").upload(path, file);
    if (error) {
      toast.error(error.message);
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from("product-images").getPublicUrl(path);
    setImageUrl(data.publicUrl);
    setUploading(false);
  };

  const submit = async () => {
    if (!user) {
      toast.error("Please sign in to leave a review");
      return;
    }
    if (rating < 1) {
      toast.error("Please choose a star rating");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("product_reviews").insert({
      product_id: productId,
      user_id: user.id,
      customer_name: name || "Customer",
      rating,
      comment: comment.trim() || null,
      image_url: imageUrl,
      status: "approved",
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Thank you for your review!");
    setRating(0);
    setComment("");
    setImageUrl(null);
    load();
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 border-0 shadow-soft">
        <h3 className="font-semibold mb-3">Write a review</h3>
        {!user ? (
          <p className="text-sm text-muted-foreground">
            Please <Link to="/auth" className="text-primary underline">sign in</Link> to write a review.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">Your rating:</span>
              <Stars value={rating} size={22} onChange={setRating} />
            </div>
            <Textarea
              rows={3}
              placeholder="Share your thoughts about fabric, fit, quality..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
            />
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm rounded-full border border-dashed px-3 py-1.5 cursor-pointer hover:border-primary transition-smooth">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleImage(e.target.files[0])}
                />
                <Upload className="h-4 w-4" />
                {uploading ? "Uploading..." : imageUrl ? "Change image" : "Add image (optional)"}
              </label>
              {imageUrl && (
                <div className="relative">
                  <img src={imageUrl} alt="" className="h-16 w-16 rounded-lg object-cover border" />
                  <button
                    type="button"
                    onClick={() => setImageUrl(null)}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground grid place-items-center"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
              <Button
                variant="hero"
                onClick={submit}
                disabled={submitting}
                className="ml-auto"
              >
                {submitting ? "Submitting..." : "Submit review"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <div className="space-y-4">
        <h3 className="font-semibold">Customer reviews ({reviews.length})</h3>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : reviews.length === 0 ? (
          <p className="text-sm text-muted-foreground">No reviews yet. Be the first to share your experience!</p>
        ) : (
          reviews.map((r) => (
            <Card key={r.id} className="p-4 border-0 shadow-soft">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm">{r.customer_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Stars value={r.rating} size={14} />
                    <span className="text-xs text-muted-foreground">{formatDate(r.created_at)}</span>
                  </div>
                </div>
              </div>
              {r.comment && <p className="text-sm mt-3 whitespace-pre-line text-foreground/85">{r.comment}</p>}
              {r.image_url && (
                <img src={r.image_url} alt="" className="mt-3 h-32 w-32 rounded-lg object-cover border" loading="lazy" />
              )}
              {r.admin_reply && (
                <div className="mt-3 rounded-lg bg-primary/5 border-l-2 border-primary p-3">
                  <p className="text-xs font-semibold text-primary inline-flex items-center gap-1.5">
                    <MessageSquare className="h-3 w-3" /> Rupshoili replied
                  </p>
                  <p className="text-sm mt-1 whitespace-pre-line text-foreground/85">{r.admin_reply}</p>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
